'use client'

import { useRef } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { CodeIcon } from '@hugeicons-pro/core-stroke-rounded'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { ToolStep } from '@/lib/commands/graph-serialize'
import { graphToolMeta, graphToolMetaByName } from '@/lib/commands/graph-tools-meta'
import { TemplateChipBar } from '../TemplateChipBar'

export function ToolStepEditor({
  step,
  priorStepIds,
  includeEvent,
  onChange,
}: {
  step: ToolStep
  priorStepIds: string[]
  includeEvent: boolean
  onChange: (step: ToolStep) => void
}) {
  const selectedTool = graphToolMetaByName.get(step.tool) ?? graphToolMeta[0]

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Tool</Label>
        <Select
          value={step.tool}
          onValueChange={(tool) => onChange({ ...step, tool, args: {} })}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Pick a tool" />
          </SelectTrigger>
          <SelectContent>
            {graphToolMeta.map((tool) => (
              <SelectItem key={tool.name} value={tool.name}>
                <span className="flex items-center gap-2">
                  <HugeiconsIcon icon={tool.icon} className="size-4" />
                  <span>{tool.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <Label>Arguments</Label>
        {selectedTool.args.length === 0 ? (
          <p className="text-sm text-atrium-black/60">This tool does not need arguments.</p>
        ) : (
          selectedTool.args.map((arg) => (
            <ArgField
              key={arg.key}
              arg={arg}
              value={getArgValue(step.args, arg)}
              priorStepIds={priorStepIds}
              includeEvent={includeEvent}
              onValueChange={(value) => {
                const nextArgs = setArgValue(step.args, arg, value)
                onChange({ ...step, args: nextArgs })
              }}
            />
          ))
        )}
      </div>

      <div className="rounded-lg bg-atrium-content px-3 py-2 text-sm text-atrium-black/70">
        Save result as <span className="font-mono text-atrium-black">{`\${$${step.id}}`}</span>
      </div>
    </div>
  )
}

function ArgField({
  arg,
  value,
  priorStepIds,
  includeEvent,
  onValueChange,
}: {
  arg: {
    key: string
    path?: string[]
    type: 'string' | 'textarea' | 'number' | 'enum'
    required?: boolean
    options?: Array<{ label: string; value: string }>
    min?: number
    max?: number
    helper?: string
  }
  value: unknown
  priorStepIds: string[]
  includeEvent: boolean
  onValueChange: (value: unknown) => void
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const isTemplate = typeof value === 'string' && value.includes('${')

  const insertToken = (token: string) => {
    const textValue = typeof value === 'string' ? value : ''
    const textarea = textareaRef.current
    if (!textarea) {
      onValueChange(`${textValue}${token}`)
      return
    }
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    onValueChange(`${textValue.slice(0, start)}${token}${textValue.slice(end)}`)
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <div className="space-y-2 rounded-lg border border-atrium-border bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <Label className="capitalize">{arg.key}</Label>
        <Button
          type="button"
          variant={isTemplate ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2 font-mono text-xs"
          onClick={() => onValueChange(isTemplate ? '' : String(value ?? ''))}
        >
          <HugeiconsIcon icon={CodeIcon} className="size-3.5" />
          {'{}'}
        </Button>
      </div>

      {isTemplate ? (
        <>
          <TemplateChipBar stepIds={priorStepIds} includeEvent={includeEvent} onInsert={insertToken} />
          <Textarea
            ref={textareaRef}
            value={String(value ?? '')}
            onChange={(event) => onValueChange(event.target.value)}
          />
        </>
      ) : arg.type === 'enum' ? (
        <Select value={String(value ?? '')} onValueChange={onValueChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select value" />
          </SelectTrigger>
          <SelectContent>
            {(arg.options ?? []).map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : arg.type === 'textarea' ? (
        <>
          <TemplateChipBar stepIds={priorStepIds} includeEvent={includeEvent} onInsert={insertToken} />
          <Textarea
            ref={textareaRef}
            value={String(value ?? '')}
            onChange={(event) => onValueChange(event.target.value)}
          />
        </>
      ) : (
        <Input
          type={arg.type === 'number' ? 'number' : 'text'}
          min={arg.min}
          max={arg.max}
          value={value == null ? '' : String(value)}
          onChange={(event) => {
            if (arg.type === 'number') {
              onValueChange(event.target.value === '' ? undefined : Number(event.target.value))
            } else {
              onValueChange(event.target.value)
            }
          }}
        />
      )}

      {arg.helper ? <p className="text-xs text-atrium-black/60">{arg.helper}</p> : null}
    </div>
  )
}

function getArgValue(args: Record<string, unknown>, arg: { key: string; path?: string[] }): unknown {
  const path = arg.path ?? [arg.key]
  let current: unknown = args
  for (const part of path) {
    if (!isRecord(current)) return undefined
    current = current[part]
  }
  return current
}

function setArgValue(args: Record<string, unknown>, arg: { key: string; path?: string[] }, value: unknown): Record<string, unknown> {
  const path = arg.path ?? [arg.key]
  const nextArgs = { ...args }

  if (value === undefined || value === '') {
    deleteArgValue(nextArgs, path)
  } else {
    let current: Record<string, unknown> = nextArgs
    for (const part of path.slice(0, -1)) {
      const existing = current[part]
      const next = isRecord(existing) ? { ...existing } : {}
      current[part] = next
      current = next
    }
    current[path[path.length - 1] ?? arg.key] = value
  }

  return nextArgs
}

function deleteArgValue(args: Record<string, unknown>, path: string[]) {
  const stack: Array<{ object: Record<string, unknown>; key: string }> = []
  let current: Record<string, unknown> = args
  for (const part of path.slice(0, -1)) {
    const next = current[part]
    if (!isRecord(next)) return
    stack.push({ object: current, key: part })
    current = next
  }

  delete current[path[path.length - 1] ?? '']
  for (const { object, key } of stack.reverse()) {
    const child = object[key]
    if (isRecord(child) && Object.keys(child).length === 0) delete object[key]
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}
